/**
 * ReviewQueueDrawerContainer — data + open-state wiring for the pipeline
 * Review-queue drawer (SE-5499, restyled SE-6554).
 *
 * Subscribes to the drawer reactive var (so any surface can open the queue),
 * drives `usePipelineReviewQueue` (skipped while closed so the three status
 * queries don't fire on every page), and forks the layout on SCREEN SIZE
 * (`useMobileScreen`) — never device type.
 * @remarks Container component — owns queries, actions, and breakpoint.
 */

import { useMemo } from "react";

import { PipelineReviewSource } from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";

import usePipelineReviewQueue, {
  type ReviewQueueSourceCounts,
} from "../../../hooks/usePipelineReviewQueue";
import { useReviewQueueDrawerState } from "../../../hooks/useReviewQueueDrawerState";
import type { ReviewQueueSourceFilter } from "../../../utils/reviewQueueRows";
import ReviewQueueDrawerView, {
  type ReviewQueueFilterOption,
} from "./ReviewQueueDrawerView";

/** Every client-side filter key (the query has no `source` arg). */
const FILTER_KEYS: readonly ReviewQueueSourceFilter[] = [
  "all",
  "automation",
  "scout_flag",
  "manual",
];

/**
 * The per-source filters, in display order, each paired with the enum it
 * counts. "all" is not here — it is prepended unconditionally and counts
 * everything.
 *
 * "Rule suggestions" rather than "Automation": PRD §7 calls the v1 queue
 * content rule suggestions, and that is the word the design's pill uses.
 */
const SOURCE_FILTERS: readonly {
  readonly key: ReviewQueueSourceFilter;
  readonly source: PipelineReviewSource;
  readonly label: string;
}[] = [
  {
    key: "automation",
    source: PipelineReviewSource.AutomationRule,
    label: "Rule suggestions",
  },
  {
    key: "scout_flag",
    source: PipelineReviewSource.ScoutFlag,
    label: "Scout flags",
  },
  { key: "manual", source: PipelineReviewSource.Manual, label: "Manual" },
];

/**
 * Build the type-filter pills from what the queue actually holds.
 *
 * Two rules, both from the PRD §7 note that v1 ships rule suggestions ALONE:
 *
 *  - a source with no rows gets no pill, so the drawer never advertises a
 *    "Scout flags · 0" filter for a feature that is not built yet;
 *  - fewer than two distinct sources means no filter row at all, because a
 *    lone "All · N" pill is a label pretending to be a control.
 *
 * The ACTIVE filter always keeps its pill even at zero — otherwise a refetch
 * that empties the selected source would strand the user in a filtered-empty
 * list with no visible way back to All.
 * @param counts - Rows per source, before filtering.
 * @param total - Total unfiltered rows.
 * @param active - The active filter.
 * @returns The pills to render; empty means "render no filter row".
 */
const buildFilterOptions = (
  counts: ReviewQueueSourceCounts,
  total: number,
  active: ReviewQueueSourceFilter
): readonly ReviewQueueFilterOption[] => {
  const shown = SOURCE_FILTERS.filter(
    entry => counts[entry.source] > 0 || entry.key === active
  );
  if (SOURCE_FILTERS.filter(entry => counts[entry.source] > 0).length < 2) {
    return [];
  }
  return [
    { key: "all", label: `All · ${total}` },
    ...shown.map(entry => ({
      key: entry.key,
      label: `${entry.label} · ${counts[entry.source]}`,
    })),
  ];
};

/**
 * Container for the Review-queue drawer.
 * @returns The wired {@link ReviewQueueDrawerView}.
 */
const ReviewQueueDrawerContainer = () => {
  const isMobile = useMobileScreen();
  const { state, close } = useReviewQueueDrawerState();

  const {
    rows,
    sourceCounts,
    totalCount,
    pendingCount,
    loading,
    isError,
    sourceFilter,
    setSourceFilter,
    decidingIds,
    accept,
    decline,
  } = usePipelineReviewQueue({
    kanbanBoardId: state.kanbanBoardId,
    // Only fetch while the drawer is open.
    skip: !state.open,
  });

  const filterOptions = useMemo(
    () => buildFilterOptions(sourceCounts, totalCount, sourceFilter),
    [sourceCounts, totalCount, sourceFilter]
  );

  /** Pre-built per-option handlers so the View never creates inline functions. */
  const filterHandlers = useMemo(
    () =>
      FILTER_KEYS.reduce(
        (acc, key) => ({ ...acc, [key]: () => setSourceFilter(key) }),
        {} as Record<ReviewQueueSourceFilter, () => void>
      ),
    [setSourceFilter]
  );

  return (
    <ReviewQueueDrawerView
      isOpen={state.open}
      onClose={close}
      isMobile={isMobile}
      rows={rows}
      loading={loading}
      isError={isError}
      pendingCount={pendingCount}
      filterOptions={filterOptions}
      sourceFilter={sourceFilter}
      filterHandlers={filterHandlers}
      decidingIds={decidingIds}
      onAccept={accept}
      onDecline={decline}
    />
  );
};

ReviewQueueDrawerContainer.displayName = "ReviewQueueDrawerContainer";

export default ReviewQueueDrawerContainer;
